import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getAdminRider, adminDeleteRiderVirtualAccount, adminCreditRiderWallet } from "../api/client";
import LoadingIndicator from "./LoadingIndicator";

const VEHICLE_ICONS = {
  bike: "🛵",
  bicycle: "🚲",
  van: "🚐",
  truck: "🚚"
};

const TX_TYPE_BADGES = {
  earning: { label: "Earning", color: "var(--success-hover)", bg: "rgba(34, 197, 94, 0.12)" },
  "commission-debit": { label: "Commission Debit", color: "var(--warning-hover)", bg: "rgba(245, 158, 11, 0.12)" },
  payout: { label: "Payout", color: "var(--info-hover)", bg: "rgba(59, 130, 246, 0.12)" },
  referral_bonus: { label: "Referral Bonus", color: "#a855f7", bg: "rgba(168, 85, 247, 0.12)" },
  wallet_funding: { label: "Wallet Funding", color: "var(--text-accent)", bg: "rgba(16, 185, 129, 0.12)" },
  topup: { label: "Top Up", color: "var(--text-accent)", bg: "rgba(16, 185, 129, 0.12)" },
  cod_offset: { label: "COD Offset", color: "#06b6d4", bg: "rgba(6, 182, 212, 0.12)" }
};

const ORDER_STATUS_BADGES = {
  completed: { cls: "info", label: "Completed" },
  delivered: { cls: "info", label: "Delivered" },
  in_transit: { cls: "warning", label: "In Transit" },
  picked_up: { cls: "warning", label: "Picked Up" },
  arrived_at_pickup: { cls: "warning", label: "Arrived at Pickup" },
  accepted: { cls: "warning", label: "Accepted" },
  scheduled: { cls: "warning", label: "Scheduled" },
  cancelled: { cls: "danger", label: "Cancelled" },
  failed: { cls: "danger", label: "Failed" }
};

function formatNaira(num) {
  return `₦${Math.round(num || 0).toLocaleString()}`;
}

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatShortDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

export default function FullDispatcherModal({
  rider,
  onClose,
  fleetOwners = [],
  onDecision,
  onBlockToggle,
  onPriorityToggle,
  onAssignFleetOwner,
  onResetPassword,
  busyId,
  fleetBusyId,
  blockReasonMap = {},
  setBlockReasonMap
}) {
  const [activeTab, setActiveTab] = useState("overview"); // overview | kyc | banking | wallet | orders | docs | actions
  const [details, setDetails] = useState(rider);
  const [loadingFresh, setLoadingFresh] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [vaActionBusy, setVaActionBusy] = useState(false);
  const [creditFormOpen, setCreditFormOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditReason, setCreditReason] = useState("Virtual Account Funding Reconciliation");
  const [creditBusy, setCreditBusy] = useState(false);
  const [actionSuccess, setActionSuccess] = useState("");
  const [actionError, setActionError] = useState("");

  const riderId = rider?._id;

  // Fetch full live dossier from backend (includes orders, wallet transactions, aggregated stats)
  const fetchLiveDetails = async () => {
    if (!riderId) return;
    setLoadingFresh(true);
    try {
      const res = await getAdminRider(riderId);
      setDetails(res.data);
    } catch (err) {
      setFetchError(err.response?.data?.error || "Could not fetch extended dispatcher telemetry");
    } finally {
      setLoadingFresh(false);
    }
  };

  const handleRemoveVirtualAccount = async () => {
    if (!window.confirm("Are you sure you want to remove this virtual account for this dispatcher? This will reset the account and allow generating a new one.")) return;
    setVaActionBusy(true);
    setActionError("");
    setActionSuccess("");
    try {
      await adminDeleteRiderVirtualAccount(riderId);
      setActionSuccess("Virtual account removed successfully.");
      await fetchLiveDetails();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to remove virtual account.");
    } finally {
      setVaActionBusy(false);
    }
  };

  const handleCreditWallet = async (e) => {
    e.preventDefault();
    const amount = Number(creditAmount);
    if (!amount || amount <= 0) {
      setActionError("Please enter a valid credit amount.");
      return;
    }
    setCreditBusy(true);
    setActionError("");
    setActionSuccess("");
    try {
      await adminCreditRiderWallet(riderId, amount, creditReason);
      setActionSuccess(`Successfully credited ₦${amount.toLocaleString()} to dispatcher wallet.`);
      setCreditAmount("");
      setCreditFormOpen(false);
      await fetchLiveDetails();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to credit dispatcher wallet.");
    } finally {
      setCreditBusy(false);
    }
  };

  useEffect(() => {
    fetchLiveDetails();
  }, [riderId]);

  if (!rider) return null;

  const data = details || rider;
  const net = data.wallet?.netBalance ?? 0;
  const isNegative = net < 0;
  const vIcon = VEHICLE_ICONS[data.vehicleType] || "🛵";

  // Calculate age from dateOfBirth if available
  let ageDisplay = "-";
  if (data.kyc?.dateOfBirth) {
    const dob = new Date(data.kyc.dateOfBirth);
    const diffMs = Date.now() - dob.getTime();
    const ageDate = new Date(diffMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    if (!isNaN(age)) ageDisplay = `${age} years (${formatShortDate(data.kyc.dateOfBirth)})`;
  }

  const kycFullName = [data.kyc?.surname, data.kyc?.firstName, data.kyc?.middleName]
    .filter(Boolean)
    .join(" ");

  const hasDocs = data.verificationDocuments && data.verificationDocuments.length > 0;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.78)",
        backdropFilter: "blur(6px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        boxSizing: "border-box"
      }}
      onClick={onClose}
    >
      <div
        className="delivery-card"
        style={{
          maxWidth: "880px",
          width: "100%",
          margin: 0,
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          boxSizing: "border-box",
          padding: "24px",
          borderRadius: "14px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
          border: "1px solid var(--border)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER SECTION */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "30px",
                border: "1px solid var(--border)",
                position: "relative"
              }}
            >
              {vIcon}
              <span
                style={{
                  position: "absolute",
                  bottom: "2px",
                  right: "2px",
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: data.isOnline ? "var(--success-hover)" : "var(--text-muted)",
                  border: "2px solid var(--bg-card)"
                }}
                title={data.isOnline ? "Online" : "Offline"}
              />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 700 }}>{data.name}</h2>
                {data.priorityStatus === "priority" && (
                  <span className="status-badge info" style={{ fontSize: "11px", padding: "2px 8px" }}>
                    ⚡ PRIORITY
                  </span>
                )}
                <span
                  className={`status-badge ${
                    data.approvalStatus === "approved"
                      ? data.isBlocked
                        ? "danger"
                        : "info"
                      : data.approvalStatus === "pending"
                      ? "warning"
                      : "danger"
                  }`}
                  style={{ fontSize: "11px", padding: "2px 8px" }}
                >
                  {data.isBlocked
                    ? "🚫 SUSPENDED"
                    : data.approvalStatus === "approved"
                    ? "APPROVED FLEET"
                    : data.approvalStatus === "pending"
                    ? "PENDING APPROVAL"
                    : data.approvalStatus?.toUpperCase()}
                </span>
                <span style={{ fontSize: "12px", color: data.isOnline ? "var(--success-hover)" : "var(--text-muted)", fontWeight: 600 }}>
                  {data.isOnline ? "🟢 Online" : "⚫ Offline"}
                </span>
              </div>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "var(--text-muted)" }}>
                📞 {data.phone} · ✉️ {data.email} · 📍 {data.city || "Lagos"} · ⭐ {data.rating ? data.rating.toFixed(1) : "5.0"}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              className="secondary"
              style={{ width: "auto", padding: "6px 14px", margin: 0, fontSize: "13px" }}
              onClick={onClose}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* SUSPENSION REASON BANNER (IF BLOCKED) */}
        {data.isBlocked && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid var(--danger)",
              color: "var(--danger-hover)",
              fontSize: "13px",
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <span>
              <strong>🚫 Account Suspended:</strong> {data.blockReason || "Policy violation / Negative balance"}
            </span>
            {onBlockToggle && (
              <button
                className="secondary"
                style={{ width: "auto", padding: "4px 10px", margin: 0, fontSize: "12px", color: "var(--text-primary)" }}
                disabled={busyId === data._id}
                onClick={() => onBlockToggle(data, false)}
              >
                Lift Suspension
              </button>
            )}
          </div>
        )}

        {/* ACTION FEEDBACK BANNERS */}
        {actionSuccess && (
          <div style={{ padding: "10px 14px", borderRadius: "8px", background: "rgba(34, 197, 94, 0.15)", border: "1px solid var(--success)", color: "var(--success-hover)", fontSize: "13px", marginBottom: "16px" }}>
            ✓ {actionSuccess}
          </div>
        )}
        {actionError && (
          <div style={{ padding: "10px 14px", borderRadius: "8px", background: "rgba(239, 68, 68, 0.15)", border: "1px solid var(--danger)", color: "var(--danger-hover)", fontSize: "13px", marginBottom: "16px" }}>
            ⚠ {actionError}
          </div>
        )}

        {/* KPI OVERVIEW GRID (4 TILES) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "20px" }}>
          {/* NET BALANCE */}
          <div className="quote-box" style={{ margin: 0, padding: "12px 14px", background: "rgba(255, 255, 255, 0.03)" }}>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block" }}>Net Wallet Balance</span>
            <div style={{ fontSize: "20px", fontWeight: 700, marginTop: "2px", color: isNegative ? "var(--danger)" : "var(--text-accent)" }}>
              {isNegative ? `-${formatNaira(Math.abs(net))}` : formatNaira(net)}
            </div>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginTop: "2px" }}>
              Digital: {formatNaira(data.wallet?.earningsBalance)} · COD Owed: {formatNaira(data.wallet?.cashOwedBalance)}
            </span>
          </div>

          {/* DELIVERIES COMPLETED */}
          <div className="quote-box" style={{ margin: 0, padding: "12px 14px", background: "rgba(255, 255, 255, 0.03)" }}>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block" }}>Deliveries Completed</span>
            <div style={{ fontSize: "20px", fontWeight: 700, marginTop: "2px", color: "var(--text-primary)" }}>
              {data.stats?.completed ?? 0}
            </div>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginTop: "2px" }}>
              Gross Value: {formatNaira(data.stats?.totalDeliveryEarnings)}
            </span>
          </div>

          {/* OUTSIDE CASH / COD HELD */}
          <div className="quote-box" style={{ margin: 0, padding: "12px 14px", background: "rgba(255, 255, 255, 0.03)" }}>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block" }}>COD Cash in Hand</span>
            <div style={{ fontSize: "20px", fontWeight: 700, marginTop: "2px", color: data.unremittedCashTotal > 0 ? "var(--warning-hover)" : "var(--text-primary)" }}>
              {formatNaira(data.unremittedCashTotal || 0)}
            </div>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginTop: "2px" }}>
              {data.unremittedCodCount || 0} unremitted COD order{data.unremittedCodCount === 1 ? "" : "s"}
            </span>
          </div>

          {/* VEHICLE & ACTIVE ORDERS */}
          <div className="quote-box" style={{ margin: 0, padding: "12px 14px", background: "rgba(255, 255, 255, 0.03)" }}>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block" }}>Vehicle &amp; Orders</span>
            <div style={{ fontSize: "16px", fontWeight: 700, marginTop: "4px", color: "var(--text-primary)" }}>
              {data.vehicleType?.toUpperCase()} {data.vehiclePlateNumber ? `(${data.vehiclePlateNumber})` : ""}
            </div>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginTop: "2px" }}>
              Active Jobs: {data.stats?.active ?? 0} · Cancelled: {data.stats?.cancelled ?? 0}
            </span>
          </div>
        </div>

        {/* NAVIGATION TABS */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            borderBottom: "1px solid var(--border)",
            paddingBottom: "10px",
            marginBottom: "18px",
            overflowX: "auto"
          }}
        >
          {[
            { id: "overview", label: "👤 Profile & Info" },
            { id: "kyc", label: "🪪 Full KYC & Guarantor" },
            { id: "banking", label: "💳 Banking & Payout" },
            { id: "wallet", label: `💰 Wallet & Ledger (${data.transactions?.length || 0})` },
            { id: "orders", label: `📦 Deliveries (${data.orders?.length || 0})` },
            { id: "docs", label: `📄 Documents (${data.verificationDocuments?.length || 0})` },
            { id: "actions", label: "⚙️ Admin Actions" }
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              className={activeTab === t.id ? "" : "secondary"}
              style={{
                width: "auto",
                padding: "6px 14px",
                margin: 0,
                fontSize: "12px",
                whiteSpace: "nowrap",
                fontWeight: activeTab === t.id ? 700 : 500
              }}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* TAB 1: OVERVIEW & PROFILE */}
        {activeTab === "overview" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "14px" }}>
              <div className="quote-box" style={{ margin: 0, padding: "14px", background: "rgba(255, 255, 255, 0.02)" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: "14px", color: "var(--text-accent)" }}>📌 Personal &amp; Contact</h4>
                <div className="info-row"><span className="label">Full Registered Name</span><span className="value">{data.name}</span></div>
                <div className="info-row"><span className="label">Phone Number</span><span className="value">{data.phone}</span></div>
                <div className="info-row"><span className="label">Email Address</span><span className="value">{data.email}</span></div>
                <div className="info-row"><span className="label">WhatsApp</span><span className="value">{data.kyc?.whatsappNumber || data.phone || "-"}</span></div>
                <div className="info-row"><span className="label">Operating City</span><span className="value">{data.city || "Lagos"}</span></div>
                <div className="info-row"><span className="label">Registered On</span><span className="value">{formatDate(data.createdAt)}</span></div>
                <div className="info-row"><span className="label">Referral Code</span><span className="value">{data.referralCode || "-"}</span></div>
                <div className="info-row"><span className="label">Referred By</span><span className="value">{data.referredByCode || "Direct Registration"}</span></div>
              </div>

              <div className="quote-box" style={{ margin: 0, padding: "14px", background: "rgba(255, 255, 255, 0.02)" }}>
                <h4 style={{ margin: "0 0 10px", fontSize: "14px", color: "var(--text-accent)" }}>🛵 Dispatch &amp; Vehicle</h4>
                <div className="info-row"><span className="label">Vehicle Type</span><span className="value">{vIcon} {data.vehicleType?.toUpperCase() || "BIKE"}</span></div>
                <div className="info-row"><span className="label">Plate Number</span><span className="value">{data.vehiclePlateNumber || "None (Bicycle or Unregistered)"}</span></div>
                <div className="info-row"><span className="label">Vehicle Ownership</span><span className="value">{{ owned: "Owns vehicle", leased: "Leased / Rented", no_access: "No access" }[data.kyc?.vehicleOwnership] || "-"}</span></div>
                <div className="info-row"><span className="label">Priority Status</span><span className="value">{data.priorityStatus === "priority" ? "⚡ Priority Dispatcher" : "Regular"}</span></div>
                <div className="info-row"><span className="label">Rating</span><span className="value">⭐ {data.rating ? data.rating.toFixed(1) : "5.0"} / 5.0</span></div>
                <div className="info-row">
                  <span className="label">Fleet Owner</span>
                  <span className="value">
                    {data.fleetOwnerId ? (typeof data.fleetOwnerId === "object" ? (data.fleetOwnerId.companyName || data.fleetOwnerId.name) : "Assigned") : "Independent"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Current Location</span>
                  <span className="value">
                    {data.currentLocation?.lat && data.currentLocation?.lng
                      ? `${data.currentLocation.lat.toFixed(4)}, ${data.currentLocation.lng.toFixed(4)}`
                      : "Not available"}
                  </span>
                </div>
                <div className="info-row"><span className="label">Last Location Ping</span><span className="value">{formatDate(data.currentLocation?.updatedAt)}</span></div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: FULL KYC & GUARANTOR */}
        {activeTab === "kyc" && (
          <div>
            {data.kyc?.submittedAt ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "14px" }}>
                {/* PERSONAL IDENTIFICATION */}
                <div className="quote-box" style={{ margin: 0, padding: "14px", background: "rgba(255, 255, 255, 0.02)" }}>
                  <h4 style={{ margin: "0 0 10px", fontSize: "14px", color: "var(--text-accent)" }}>🪪 Identity &amp; NIN Verification</h4>
                  <div className="info-row"><span className="label">Full Legal Name</span><span className="value">{kycFullName || data.name}</span></div>
                  <div className="info-row"><span className="label">National Identity (NIN)</span><span className="value" style={{ fontWeight: 700, color: "var(--info-hover)" }}>{data.kyc.nin || "-"}</span></div>
                  <div className="info-row"><span className="label">Date of Birth &amp; Age</span><span className="value">{ageDisplay}</span></div>
                  <div className="info-row"><span className="label">Gender</span><span className="value">{data.kyc.gender ? data.kyc.gender.toUpperCase() : "-"}</span></div>
                  <div className="info-row"><span className="label">Marital Status</span><span className="value">{data.kyc.maritalStatus ? data.kyc.maritalStatus.toUpperCase() : "-"}</span></div>
                  <div className="info-row"><span className="label">WhatsApp Contact</span><span className="value">{data.kyc.whatsappNumber || "-"}</span></div>
                  <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                    <span className="label">Residential Address</span>
                    <span className="value" style={{ marginTop: "4px" }}>{data.kyc.residentialAddress || "-"}</span>
                  </div>
                </div>

                {/* DRIVING & VEHICLE CREDENTIALS */}
                <div className="quote-box" style={{ margin: 0, padding: "14px", background: "rgba(255, 255, 255, 0.02)" }}>
                  <h4 style={{ margin: "0 0 10px", fontSize: "14px", color: "var(--text-accent)" }}>🛵 Driving &amp; Vehicle Credentials</h4>
                  <div className="info-row"><span className="label">Driving License #</span><span className="value" style={{ fontWeight: 600 }}>{data.kyc.drivingLicenseNumber || "-"}</span></div>
                  <div className="info-row"><span className="label">Vehicle Registration #</span><span className="value">{data.kyc.vehicleRegistrationNumber || "-"}</span></div>
                  <div className="info-row"><span className="label">Vehicle Ownership</span><span className="value">{{ owned: "Owns bike/vehicle", leased: "Leased / Rented", no_access: "No access to vehicle" }[data.kyc.vehicleOwnership] || "-"}</span></div>
                  <div className="info-row"><span className="label">Riding Experience</span><span className="value">{data.kyc.ridingExperienceYears ? `${data.kyc.ridingExperienceYears} years` : "-"}</span></div>
                  <div className="info-row"><span className="label">Driving Experience</span><span className="value">{data.kyc.drivingExperienceYears ? `${data.kyc.drivingExperienceYears} years` : "-"}</span></div>
                  <div className="info-row">
                    <span className="label">Lagos LASDRI Card</span>
                    <span className="value" style={{ color: data.kyc.hasValidLasdri ? "var(--success-hover)" : "var(--text-muted)" }}>
                      {data.kyc.hasValidLasdri ? "✓ Valid LASDRI Certified" : "No / Not Applicable"}
                    </span>
                  </div>
                  <div className="info-row"><span className="label">KYC Submitted At</span><span className="value">{formatDate(data.kyc.submittedAt)}</span></div>
                </div>

                {/* GUARANTOR / EMERGENCY REFERENCE */}
                <div className="quote-box" style={{ margin: 0, padding: "14px", background: "rgba(255, 255, 255, 0.02)", gridColumn: "1 / -1" }}>
                  <h4 style={{ margin: "0 0 10px", fontSize: "14px", color: "var(--text-accent)" }}>📇 Guarantor / Reference Contact</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px" }}>
                    <div className="info-row"><span className="label">Guarantor Name</span><span className="value" style={{ fontWeight: 600 }}>{data.kyc.guarantor?.name || "-"}</span></div>
                    <div className="info-row"><span className="label">Guarantor Phone</span><span className="value">{data.kyc.guarantor?.phone || "-"}</span></div>
                    <div className="info-row" style={{ gridColumn: "1 / -1", flexDirection: "column", alignItems: "flex-start" }}>
                      <span className="label">Guarantor Residential Address</span>
                      <span className="value" style={{ marginTop: "4px" }}>{data.kyc.guarantor?.address || "-"}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state" style={{ padding: "30px 10px" }}>
                <span className="empty-state-icon">⚠️</span>
                <p className="empty-state-text">No structured KYC on file. This dispatcher registered before the comprehensive KYC onboarding form was introduced.</p>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: BANKING & PAYOUT ACCOUNTS */}
        {activeTab === "banking" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "14px" }}>
            {/* MONEY OUT: PAYOUT SUBACCOUNT */}
            <div className="quote-box" style={{ margin: 0, padding: "16px", background: "rgba(255, 255, 255, 0.02)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <h4 style={{ margin: 0, fontSize: "15px", color: "var(--text-accent)" }}>💸 Payout Account (Money Out)</h4>
                <span className={`status-badge ${data.payoutAccount?.status === "active" ? "info" : "warning"}`} style={{ fontSize: "11px" }}>
                  {data.payoutAccount?.status === "active" ? "ACTIVE" : data.payoutAccount?.status || "NOT CONFIGURED"}
                </span>
              </div>
              <p className="hint" style={{ margin: "0 0 12px", fontSize: "12px" }}>
                The bank account where this dispatcher receives earnings withdrawals via automated sweep or manual on-demand payout.
              </p>

              {data.payoutAccount?.status === "active" ? (
                <>
                  <div className="info-row"><span className="label">Payout Provider</span><span className="value" style={{ textTransform: "capitalize" }}>{data.payoutAccount.provider || "-"}</span></div>
                  <div className="info-row"><span className="label">Bank Name / Code</span><span className="value">{data.payoutAccount.bankName || data.payoutAccount.bankCode || "-"}</span></div>
                  <div className="info-row"><span className="label">Account Number</span><span className="value" style={{ fontWeight: 700, letterSpacing: "1px" }}>{data.payoutAccount.accountNumber}</span></div>
                  <div className="info-row"><span className="label">Account Holder Name</span><span className="value" style={{ fontWeight: 600 }}>{data.payoutAccount.accountName}</span></div>
                  <div className="info-row"><span className="label">Subaccount Code</span><span className="value" style={{ fontFamily: "monospace", fontSize: "12px" }}>{data.payoutAccount.subaccountCode || "-"}</span></div>
                  <div className="info-row">
                    <span className="label">Sweep Schedule</span>
                    <span className="value" style={{ fontWeight: 600, color: "var(--info-hover)" }}>
                      {{ instant: "⚡ Instant (On delivery completion)", daily: "🌙 Daily (Nightly auto-sweep)", manual: "✋ Manual (Dispatcher withdraws on demand)" }[data.payoutAccount.sweepSchedule] || data.payoutAccount.sweepSchedule || "Manual"}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">Preferred Withdrawal Amount</span>
                    <span className="value" style={{ fontWeight: 600, color: "var(--text-accent)" }}>
                      {data.payoutAccount.preferredAmount ? formatNaira(data.payoutAccount.preferredAmount) : "Full Available Balance (No minimum cap)"}
                    </span>
                  </div>
                </>
              ) : (
                <div style={{ padding: "16px", textAlign: "center", background: "rgba(255,255,255,0.02)", borderRadius: "8px" }}>
                  <p style={{ margin: 0, fontSize: "13px", color: "var(--text-muted)" }}>Payout account not yet configured by dispatcher.</p>
                </div>
              )}
            </div>

            {/* MONEY IN: DEDICATED VIRTUAL ACCOUNT */}
            <div className="quote-box" style={{ margin: 0, padding: "16px", background: "rgba(255, 255, 255, 0.02)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <h4 style={{ margin: 0, fontSize: "15px", color: "var(--text-accent)" }}>🏦 Virtual Account (Money In)</h4>
                <span className={`status-badge ${data.virtualAccount?.status === "active" ? "info" : "warning"}`} style={{ fontSize: "11px" }}>
                  {data.virtualAccount?.status === "active" ? "ACTIVE" : data.virtualAccount?.status || "NOT CREATED"}
                </span>
              </div>
              <p className="hint" style={{ margin: "0 0 12px", fontSize: "12px" }}>
                Dedicated NUBAN bank account number for direct bank transfers to fund dispatcher wallet or settle COD debts.
              </p>

              {data.virtualAccount?.status === "active" ? (
                <>
                  <div className="info-row"><span className="label">Provider</span><span className="value" style={{ textTransform: "capitalize" }}>{data.virtualAccount.provider || "-"}</span></div>
                  <div className="info-row"><span className="label">Bank Name</span><span className="value" style={{ fontWeight: 600 }}>{data.virtualAccount.bankName || "-"}</span></div>
                  <div className="info-row"><span className="label">Virtual NUBAN</span><span className="value" style={{ fontWeight: 700, fontSize: "16px", letterSpacing: "1px", color: "var(--text-accent)" }}>{data.virtualAccount.accountNumber}</span></div>
                  <div className="info-row"><span className="label">Account Name</span><span className="value">{data.virtualAccount.accountName}</span></div>
                  <div className="info-row"><span className="label">Customer Code</span><span className="value" style={{ fontFamily: "monospace", fontSize: "12px" }}>{data.virtualAccount.customerCode || "-"}</span></div>
                  <div style={{ marginTop: "14px", display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      disabled={vaActionBusy}
                      onClick={handleRemoveVirtualAccount}
                      style={{
                        padding: "6px 14px",
                        background: "rgba(239, 68, 68, 0.12)",
                        color: "var(--danger-hover)",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 600
                      }}
                    >
                      {vaActionBusy ? "Removing..." : "🗑️ Remove Virtual Account"}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ padding: "16px", textAlign: "center", background: "rgba(255,255,255,0.02)", borderRadius: "8px" }}>
                  <p style={{ margin: 0, fontSize: "13px", color: "var(--text-muted)" }}>Dedicated virtual account not yet provisioned.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 4: WALLET & RECENT TRANSACTIONS */}
        {activeTab === "wallet" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px", marginBottom: "16px" }}>
              <div className="quote-box" style={{ margin: 0, padding: "10px 12px" }}>
                <span className="label" style={{ fontSize: "11px" }}>Digital Earnings Balance</span>
                <strong style={{ fontSize: "16px", display: "block", color: "var(--info-hover)", marginTop: "2px" }}>
                  {formatNaira(data.wallet?.earningsBalance)}
                </strong>
              </div>
              <div className="quote-box" style={{ margin: 0, padding: "10px 12px" }}>
                <span className="label" style={{ fontSize: "11px" }}>COD Commission Owed</span>
                <strong style={{ fontSize: "16px", display: "block", color: "var(--danger-hover)", marginTop: "2px" }}>
                  {formatNaira(data.wallet?.cashOwedBalance)}
                </strong>
              </div>
              <div className="quote-box" style={{ margin: 0, padding: "10px 12px" }}>
                <span className="label" style={{ fontSize: "11px" }}>Outside COD Cash Held</span>
                <strong style={{ fontSize: "16px", display: "block", color: "var(--warning-hover)", marginTop: "2px" }}>
                  {formatNaira(data.unremittedCashTotal)} ({data.unremittedCodCount || 0} orders)
                </strong>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h4 style={{ margin: 0, fontSize: "14px" }}>Recent Wallet Ledger Transactions</h4>
              <button
                type="button"
                className="secondary"
                style={{ width: "auto", margin: 0, padding: "5px 12px", fontSize: "12px", color: "var(--text-accent)" }}
                onClick={() => setCreditFormOpen((prev) => !prev)}
              >
                {creditFormOpen ? "✕ Cancel Credit" : "➕ Reconcile / Credit Wallet"}
              </button>
            </div>

            {creditFormOpen && (
              <form onSubmit={handleCreditWallet} style={{ padding: "14px", background: "rgba(255,255,255,0.03)", borderRadius: "8px", border: "1px solid var(--border)", marginBottom: "16px" }}>
                <h5 style={{ margin: "0 0 10px", fontSize: "13px", color: "var(--text-accent)" }}>Manual Wallet Credit / Virtual Account Reconciliation</h5>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr auto", gap: "10px", alignItems: "flex-end" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", marginBottom: "4px" }}>Amount (₦)</label>
                    <input
                      type="number"
                      placeholder="e.g. 5000"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-primary)" }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", marginBottom: "4px" }}>Reason / Memo</label>
                    <input
                      type="text"
                      placeholder="Virtual Account Funding Reconciliation"
                      value={creditReason}
                      onChange={(e) => setCreditReason(e.target.value)}
                      style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-primary)" }}
                    />
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={creditBusy}
                      style={{ margin: 0, padding: "8px 16px", fontSize: "12px", background: "var(--accent)", color: "#000", fontWeight: 600, border: "none", borderRadius: "6px", cursor: "pointer" }}
                    >
                      {creditBusy ? "Crediting..." : "Credit Wallet"}
                    </button>
                  </div>
                </div>
              </form>
            )}
            {loadingFresh ? (
              <LoadingIndicator label="Loading transaction ledger..." />
            ) : !data.transactions || data.transactions.length === 0 ? (
              <p className="hint">No transactions recorded in wallet ledger yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                      <th style={{ padding: "8px 6px" }}>Date</th>
                      <th style={{ padding: "8px 6px" }}>Type</th>
                      <th style={{ padding: "8px 6px" }}>Amount</th>
                      <th style={{ padding: "8px 6px" }}>Gateway / Ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((tx) => {
                      const badge = TX_TYPE_BADGES[tx.type] || { label: tx.type, color: "var(--text-muted)", bg: "transparent" };
                      const isCredit = ["earning", "referral_bonus", "wallet_funding", "topup"].includes(tx.type);
                      return (
                        <tr key={tx._id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "8px 6px", whiteSpace: "nowrap" }}>{formatDate(tx.createdAt)}</td>
                          <td style={{ padding: "8px 6px" }}>
                            <span style={{ padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, color: badge.color, backgroundColor: badge.bg }}>
                              {badge.label}
                            </span>
                          </td>
                          <td style={{ padding: "8px 6px", fontWeight: 700, color: isCredit ? "var(--success-hover)" : "var(--danger-hover)" }}>
                            {isCredit ? `+${formatNaira(tx.amount)}` : `-${formatNaira(tx.amount)}`}
                          </td>
                          <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: "11px", color: "var(--text-muted)" }}>
                            {tx.reference || tx.gateway || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 5: DELIVERIES & ORDER HISTORY */}
        {activeTab === "orders" && (
          <div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px" }}>
              <h4 style={{ margin: 0, fontSize: "14px" }}>Recent Orders &amp; Delivery History</h4>
              <span className="hint" style={{ margin: 0 }}>Showing last {data.orders?.length || 0} deliveries</span>
            </div>

            {loadingFresh ? (
              <LoadingIndicator label="Loading delivery history..." />
            ) : !data.orders || data.orders.length === 0 ? (
              <p className="hint">No deliveries on record for this dispatcher.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                      <th style={{ padding: "8px 6px" }}>Tracking ID</th>
                      <th style={{ padding: "8px 6px" }}>Route (Pickup ➡️ Dropoff)</th>
                      <th style={{ padding: "8px 6px" }}>Package / Vehicle</th>
                      <th style={{ padding: "8px 6px" }}>Fare</th>
                      <th style={{ padding: "8px 6px" }}>Payment</th>
                      <th style={{ padding: "8px 6px" }}>Status</th>
                      <th style={{ padding: "8px 6px" }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orders.map((o) => {
                      const st = ORDER_STATUS_BADGES[o.status] || { cls: "", label: o.status };
                      return (
                        <tr key={o._id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "8px 6px", fontWeight: 700, fontFamily: "monospace" }}>{o.trackingId}</td>
                          <td style={{ padding: "8px 6px", maxWidth: "260px" }}>
                            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              📍 {o.pickup?.address}
                            </div>
                            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--text-muted)" }}>
                              🏁 {o.dropoff?.address}
                            </div>
                            {o.isInterstate && (
                              <span className="status-badge warning" style={{ fontSize: "10px", padding: "1px 5px", marginTop: "2px", display: "inline-block" }}>
                                🛣️ Interstate
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "8px 6px" }}>
                            <div>{o.vehicleType?.toUpperCase()}</div>
                            <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{o.packageType}</div>
                          </td>
                          <td style={{ padding: "8px 6px", fontWeight: 700, color: "var(--text-accent)" }}>
                            {formatNaira(o.price)}
                          </td>
                          <td style={{ padding: "8px 6px", textTransform: "capitalize" }}>{o.paymentMethod || "card"}</td>
                          <td style={{ padding: "8px 6px" }}>
                            <span className={`status-badge ${st.cls}`} style={{ fontSize: "11px" }}>
                              {st.label}
                            </span>
                          </td>
                          <td style={{ padding: "8px 6px", whiteSpace: "nowrap", color: "var(--text-muted)" }}>
                            {formatShortDate(o.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 6: DOCUMENTS */}
        {activeTab === "docs" && (
          <div>
            <h4 style={{ margin: "0 0 10px", fontSize: "14px" }}>Verification &amp; Identity Documents</h4>
            {!hasDocs ? (
              <div className="empty-state" style={{ padding: "30px 10px" }}>
                <span className="empty-state-icon">📄</span>
                <p className="empty-state-text">No uploaded verification documents on file for this dispatcher.</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "12px" }}>
                {data.verificationDocuments.map((doc, idx) => (
                  <div key={idx} className="quote-box" style={{ margin: 0, padding: "14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <strong style={{ fontSize: "13px", textTransform: "capitalize", display: "block" }}>
                        📁 {doc.type.replace(/_/g, " ")}
                      </strong>
                      <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                        Uploaded {formatShortDate(doc.uploadedAt)}
                      </span>
                    </div>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button secondary"
                      style={{ width: "auto", padding: "4px 10px", margin: 0, fontSize: "12px", textDecoration: "none" }}
                    >
                      View ↗
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 7: ADMIN ACTIONS & MANAGEMENT */}
        {activeTab === "actions" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {/* PENDING APPROVAL CONTROLS */}
            {data.approvalStatus === "pending" && onDecision && (
              <div className="quote-box" style={{ margin: 0, padding: "16px", background: "rgba(245, 158, 11, 0.05)", border: "1px solid var(--warning)" }}>
                <h4 style={{ margin: "0 0 8px", fontSize: "14px", color: "var(--warning-hover)" }}>⌛ Pending Application Decision</h4>
                <p style={{ margin: "0 0 12px", fontSize: "13px" }}>Review the dispatcher KYC and credentials, then approve or reject their application.</p>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    style={{ width: "auto", padding: "8px 20px", margin: 0 }}
                    disabled={busyId === data._id}
                    onClick={() => onDecision(data._id, "approved")}
                  >
                    ✓ Approve Dispatcher
                  </button>
                  <button
                    className="secondary"
                    style={{ width: "auto", padding: "8px 20px", margin: 0, color: "var(--danger-hover)" }}
                    disabled={busyId === data._id}
                    onClick={() => onDecision(data._id, "rejected")}
                  >
                    ✕ Reject Application
                  </button>
                </div>
              </div>
            )}

            {/* PRIORITY DISPATCH TOGGLE */}
            {onPriorityToggle && (
              <div className="quote-box" style={{ margin: 0, padding: "14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong style={{ fontSize: "14px", display: "block" }}>⚡ Priority Dispatch Status</strong>
                  <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                    Priority dispatchers receive matching preference ahead of regular fleet when jobs are dispatched.
                  </span>
                </div>
                <button
                  className="secondary"
                  style={{ width: "auto", padding: "6px 14px", margin: 0, fontSize: "12px" }}
                  disabled={busyId === data._id}
                  onClick={() => onPriorityToggle(data)}
                >
                  {data.priorityStatus === "priority" ? "Remove Priority Status" : "⚡ Mark as Priority"}
                </button>
              </div>
            )}

            {/* FLEET OWNER ASSIGNMENT */}
            {onAssignFleetOwner && (
              <div className="quote-box" style={{ margin: 0, padding: "14px" }}>
                <strong style={{ fontSize: "14px", display: "block", marginBottom: "4px" }}>🚚 Assign Fleet Owner</strong>
                <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "10px" }}>
                  Attach this dispatcher to a registered fleet owner for sub-fleet management and consolidated reporting.
                </span>
                <select
                  value={(typeof data.fleetOwnerId === "object" ? data.fleetOwnerId?._id : data.fleetOwnerId) || ""}
                  onChange={(e) => onAssignFleetOwner(data, e.target.value)}
                  disabled={fleetBusyId === data._id}
                  style={{
                    width: "100%",
                    maxWidth: "360px",
                    margin: 0,
                    background: "var(--bg-card)",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontSize: "13px"
                  }}
                >
                  <option value="">— Independent (No Fleet Owner) —</option>
                  {fleetOwners.map((f) => (
                    <option key={f._id} value={f._id}>
                      {f.fleetName || f.companyName || f.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* SUSPEND OR UNBLOCK */}
            {onBlockToggle && (
              <div className="quote-box" style={{ margin: 0, padding: "14px" }}>
                <strong style={{ fontSize: "14px", display: "block", marginBottom: "4px" }}>🚫 Account Suspension</strong>
                <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block", marginBottom: "10px" }}>
                  Suspended dispatchers cannot go online, claim job-board orders, or accept delivery offers.
                </span>
                {data.isBlocked ? (
                  <button
                    style={{ width: "auto", padding: "6px 14px", margin: 0, fontSize: "12px" }}
                    disabled={busyId === data._id}
                    onClick={() => onBlockToggle(data, false)}
                  >
                    ✅ Lift Suspension / Unblock Dispatcher
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                    <input
                      type="text"
                      placeholder="Reason for suspension (optional)"
                      value={blockReasonMap[data._id] || ""}
                      onChange={(e) => setBlockReasonMap?.({ ...blockReasonMap, [data._id]: e.target.value })}
                      style={{ maxWidth: "320px", marginBottom: 0, padding: "6px 12px", fontSize: "13px" }}
                    />
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "6px 14px", margin: 0, fontSize: "12px", color: "var(--danger-hover)" }}
                      disabled={busyId === data._id}
                      onClick={() => onBlockToggle(data, true)}
                    >
                      🚫 Suspend Dispatcher
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* PASSWORD RESET */}
            {onResetPassword && (
              <div className="quote-box" style={{ margin: 0, padding: "14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <strong style={{ fontSize: "14px", display: "block" }}>🔑 Dispatcher Password Reset</strong>
                  <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                    Admin-initiated password override with strong password requirements.
                  </span>
                </div>
                <button
                  className="secondary"
                  style={{ width: "auto", padding: "6px 14px", margin: 0, fontSize: "12px" }}
                  onClick={() => onResetPassword(data)}
                >
                  🔑 Reset Password
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
